import { BaseEquipment, Equipment, GymTypes } from 'fitbeat.models';
import { AreaCategory, GymConfigs, isStaticFunctionalZone } from '../../config';
import {
  CardioOrResistance, Exercise, Level, WorkoutSelection,
} from '../../entities';
import { WorkoutSettings } from '../../entities/user/workoutsettings';
import {
  ICurrentWorkoutSelectionRepository,
} from '../../persistence/repositories/interfaces/ICurrentWorkoutSelectionRepository';
import { IWorkoutSelectionRepository } from '../../persistence/repositories/interfaces/iworkoutselection-repository';

export interface IWorkoutSelectionRange {
  lowerLimit: number;
  upperLimit: number;
}
export class CurrentWorkoutSelectionManager {
  public static MAX_WORKOUT_ROW = 60;

  public static activeSelectionId = (gymType: GymTypes, settings: WorkoutSettings) => {
    const referenceKey = WorkoutSelection.refSettingsString(settings);
    const gymTypeString = gymType === GymTypes.Indoor ? '' : `${gymType}_`;
    return `${gymTypeString}${referenceKey}`;
  };

  public static getAllSettingCombinations(gymType: GymTypes = GymTypes.Indoor) {
    const levels = [Level.Beginner, Level.Basic, Level.Intermediate];
    const allSettingCombinations: WorkoutSettings[] = [];
    levels.forEach((level: string) => {
      const settingsForLevel = CurrentWorkoutSelectionManager.getAllSettingsForLevel(Level[level], gymType);
      allSettingCombinations.push.apply(
        allSettingCombinations,
        settingsForLevel,
      );
    });
    return allSettingCombinations;
  }

  public static getAllStaticFunctionalZoneSettingCombinations(area: AreaCategory) {
    const levels = [Level.Beginner, Level.Basic, Level.Intermediate];
    const homeWorkouts = GymTypes.Home;
    const replaceHomeBenchWithStudioBench = true;

    const equipment = Exercise.getEquipmentForStudioArea(area);
    if (equipment === undefined) {
      throw new Error(`Failed to determine equipment for static function zone. is ${area} a static functional zone?`);
    }

    let allSettingCombinations: WorkoutSettings[] = [];
    levels.forEach((level: string) => {
      const settingsForLevel = CurrentWorkoutSelectionManager.getAllSettingsForLevel(
        Level[level],
        homeWorkouts,
        equipment,
        replaceHomeBenchWithStudioBench,
      );
      allSettingCombinations = allSettingCombinations.concat(settingsForLevel);
    });
    return allSettingCombinations;
  }

  public static getAllSettingsForLevel(
    level: Level,
    gymType: GymTypes,
    equipmentFilter: Equipment[] = [],
    replaceHomeBenchWithStudioBench = false,
  ): WorkoutSettings[] {
    const cardioOrResistanceKeys = Object.keys(CardioOrResistance);
    const allSettingCombinations: WorkoutSettings[] = [];

    cardioOrResistanceKeys.forEach((cardioOrResistance: CardioOrResistance) => {
      if (
        cardioOrResistance === CardioOrResistance.Cardio
              || cardioOrResistance === CardioOrResistance.Resistance
      ) {
        allSettingCombinations.push.apply(
          allSettingCombinations,
          GymConfigs[gymType].getWorkoutSettings(level, cardioOrResistance),
        );
      }
      // TODO: When Balance is ready for all settings this will need to include balance
      if (cardioOrResistance === CardioOrResistance.Balance) {
        const settingsForBalance = GymConfigs[gymType].getWorkoutSettingsForBalance(level);
        allSettingCombinations.push.apply(
          allSettingCombinations,
          settingsForBalance,
        );
      }
    });

    if (replaceHomeBenchWithStudioBench) {
      for (const settings of allSettingCombinations) {
        settings.equipment = BaseEquipment.replaceHomeBenchWithStudioBench(settings.equipment);
      }
      equipmentFilter = BaseEquipment.replaceHomeBenchWithStudioBench(equipmentFilter);
    }

    if (equipmentFilter.length === 0) {
      return allSettingCombinations;
    }
    return allSettingCombinations
      .filter((settings) => equipmentFilter
        .every((equipment: Equipment) => settings.equipment.indexOf(equipment) !== -1
                                         && equipmentFilter.length === settings.equipment.length));
  }

  public static initializeInBackground(
    repository: ICurrentWorkoutSelectionRepository,
    workoutSelectionRepository: IWorkoutSelectionRepository,
    logger: any,
  ) {
    const manager = new CurrentWorkoutSelectionManager(repository, workoutSelectionRepository, logger);
    repository.createDefaultDoc(manager.buildDefaultDoc())
      .catch((e) => logger.warn(e));
    return manager;
  }

  private constructor(
    private repository: ICurrentWorkoutSelectionRepository,
    private workoutSelectionRepository: IWorkoutSelectionRepository,
    private logger: any,
  ) {
  }

  public async getCurrentActiveRefNumber(gymType: GymTypes, settings: WorkoutSettings, timezone: string) {
    const workoutSelectionKey = CurrentWorkoutSelectionManager.activeSelectionId(gymType, settings);
    const activeNumber = await this.repository.getActiveWorkoutSelectionForSelectionKeyInTimezone(
      workoutSelectionKey,
      timezone,
    );
    return WorkoutSelection.refNr(activeNumber, settings, gymType);
  }

  public async getCurrentWorkoutSelection(
    gymType: GymTypes,
    settings: WorkoutSettings,
    timezone: string,
    areaCategory?: AreaCategory,
  ) {
    const activeWorkoutReferenceNumber = await this.getCurrentActiveRefNumber(
      isStaticFunctionalZone(areaCategory)
        ? GymTypes.Home
        : gymType,
      settings,
      timezone,
    );
    const isDefaultKey = activeWorkoutReferenceNumber.indexOf('-1') !== -1;
    if (isDefaultKey) {
      return null;
    }

    return this.getSelectionForReferenceNumber(activeWorkoutReferenceNumber);
  }

  public async getCurrentWorkoutSelectionOrFail(
    gymType: GymTypes,
    settings: WorkoutSettings,
    timezone: string,
  ): Promise<WorkoutSelection> {
    const current = await this.getCurrentWorkoutSelection(gymType, settings, timezone);
    if (!current) {
      const settingInfo = `${settings.cardioOrResistance} ${settings.equipment.join()}`;
      throw new Error(`failed to load workout selection for ${gymType} in ${timezone} with ${settingInfo}`);
    }
    return current;
  }

  public async getSelectionForReferenceNumber(workoutReferenceNumber: string) {
    return this.workoutSelectionRepository.get(workoutReferenceNumber);
  }

  public async determineAndSaveNextSelection(gymType: GymTypes) {
    switch (gymType) {
      case GymTypes.Home: {
        await this.determineAndSaveNextSelectionFor(
          [Level.Beginner, Level.Basic, Level.Intermediate],
          GymTypes.Home,
          BaseEquipment.getBodyWeight(),
        );
        await this.determineAndSaveNextSelectionFor(
          [Level.Beginner, Level.Basic, Level.Intermediate],
          GymTypes.Home,
          BaseEquipment.getDumbbells(),
        );
        await this.determineAndSaveNextSelectionFor(
          [Level.Beginner, Level.Basic, Level.Intermediate],
          GymTypes.Home,
          BaseEquipment.getBenchAndDumbbells(),
        );
        break;
      }
      case GymTypes.Indoor: {
        await this.determineAndSaveNextSelectionFor(
          [Level.Beginner],
          GymTypes.Indoor,
          [],
        );
        await this.determineAndSaveNextSelectionFor(
          [Level.Basic, Level.Intermediate],
          GymTypes.Indoor,
          [],
        );
        break;
      }
      case GymTypes.Outdoor: {
        await this.determineAndSaveNextSelectionFor(
          [Level.Beginner, Level.Basic, Level.Intermediate],
          GymTypes.Outdoor,
          [],
        );
      }
    }
  }

  public async setActiveWorkoutFor(gymType: GymTypes, settings: WorkoutSettings, setNumber: number) {
    const workoutSelectionKey = WorkoutSelection.refNr(setNumber, settings, gymType);
    const workoutSelection = await this.workoutSelectionRepository.get(workoutSelectionKey);
    if (!workoutSelection || !WorkoutSelection.isUsable(workoutSelection)) {
      throw new Error('only usable selections van be made active');
    }
    const selectionKey = CurrentWorkoutSelectionManager.activeSelectionId(gymType, settings);
    return this.repository.setActiveWorkoutSelectionFor(selectionKey, setNumber);
  }

  public async determineAndSaveNextSelectionFor(levels: Level[], gymType: GymTypes, equipmentFilter: Equipment[]) {
    try {
      const firstLevel = levels[0];
      const firstSetOfSettings = CurrentWorkoutSelectionManager
        .getAllSettingsForLevel(firstLevel, gymType, equipmentFilter)[0];
      const firstWorkoutSelectionKey = CurrentWorkoutSelectionManager.activeSelectionId(gymType, firstSetOfSettings);
      const currentSelectionNumberForKey = await this.repository
        .getActiveWorkoutSelectionForSelectionKey(firstWorkoutSelectionKey);

      const allWorkoutSelections = await this.findNextValidWorkoutSelectionForAllSettings(
        currentSelectionNumberForKey || 0,
        levels,
        gymType,
        0,
        equipmentFilter,
      );

      for (const workoutSelection of allWorkoutSelections) {
        const referenceKey = CurrentWorkoutSelectionManager.activeSelectionId(gymType, workoutSelection.settings);
        await this.repository.setActiveWorkoutSelectionFor(
          referenceKey,
          workoutSelection.workoutSequenceDate,
        );
      }
    } catch (error) {
      this.logger.warn(error);
    }
  }

  public async determineNextWorkoutSelection(
    currentSelectionNumberForKey: number,
    gymType: GymTypes,
    settings: WorkoutSettings,
    validRange?: IWorkoutSelectionRange,
    areaCategory?: AreaCategory,
  ) {
    const workoutSelection = await this.findNextValidWorkoutSelectionForCurrentSettings(
      currentSelectionNumberForKey || 0,
      settings,
      gymType,
      0,
      validRange,
      areaCategory,
    );
    return workoutSelection;
  }

  public copyCurrentWorkoutSelectionToTimeZoneSpecificDoc(timezone: string) {
    return this.repository.copyMasterDocumentToTimezone(timezone);
  }

  private async findNextValidWorkoutSelectionForAllSettings(
    currentSelectionNumber: number,
    levels: Level[],
    gymType: GymTypes,
    seekCount = 0,
    equipmentFilter: Equipment[] = [],
  ):
    Promise<WorkoutSelection[]> {
    const MAX_SEEK_COUNT = 100;
    if (seekCount >= MAX_SEEK_COUNT) {
      const details = {
        currentSelectionNumber,
        levels,
        gymType,
        equipmentFilter,
      };
      throw new Error(`failed to find workout selection after ${seekCount} tries for ${JSON.stringify(details)}`);
    }

    const allWorkoutSettingCombinations: WorkoutSettings[] = [];
    for (const level of levels) {
      allWorkoutSettingCombinations.push.apply(
        allWorkoutSettingCombinations,
        CurrentWorkoutSelectionManager.getAllSettingsForLevel(
          level,
          gymType,
          equipmentFilter,
          gymType === GymTypes.Home,
        ),
      );
    }
    const allWorkoutSelections: any[] = [];
    const nextSelectionNumber = (currentSelectionNumber % CurrentWorkoutSelectionManager.MAX_WORKOUT_ROW) + 1;
    for (const settings of allWorkoutSettingCombinations) {
      const nextSelectionRefNr = WorkoutSelection.refNr(nextSelectionNumber, settings, gymType);
      const nextWorkoutSelection = await this.workoutSelectionRepository.get(nextSelectionRefNr);
      if (!nextWorkoutSelection) {
        return this.findNextValidWorkoutSelectionForAllSettings(
          nextSelectionNumber,
          levels,
          gymType,
          ++seekCount,
          equipmentFilter,
        );
      }

      const isUsable = WorkoutSelection.isUsable(nextWorkoutSelection);
      allWorkoutSelections.push(isUsable ? nextWorkoutSelection : null);
    }

    const failedSelections = allWorkoutSelections.filter((workoutSelection) => workoutSelection === null);

    if (failedSelections.length === 0) {
      return allWorkoutSelections;
    }
    return this.findNextValidWorkoutSelectionForAllSettings(
      nextSelectionNumber,
      levels,
      gymType,
      ++seekCount,
      equipmentFilter,
    );
  }

  private isCurrentSelectionNumberWithinRange(currentSelectionNumber: number, range: IWorkoutSelectionRange): boolean {
    return currentSelectionNumber >= range.lowerLimit && currentSelectionNumber <= range.upperLimit;
  }

  private getNextSelectionNumber(currentSelectionNumber: number, range?: IWorkoutSelectionRange): number {
    if (!range) {
      return (currentSelectionNumber % CurrentWorkoutSelectionManager.MAX_WORKOUT_ROW) + 1;
    }
    const length = range.upperLimit - range.lowerLimit + 1;
    return ((currentSelectionNumber - range.lowerLimit + 1) % length) + range.lowerLimit;
  }

  private async findNextValidWorkoutSelectionForCurrentSettings(
    currentSelectionNumber: number,
    workoutSettings: WorkoutSettings,
    gymType: GymTypes,
    seekCount = 0,
    validRange?: IWorkoutSelectionRange,
    areaCategory?: AreaCategory,
  ): Promise<WorkoutSelection | null> {
    if (validRange && !this.isCurrentSelectionNumberWithinRange(currentSelectionNumber, validRange)) {
      throw new Error('currentSelectionNumber not within range');
    }
    const MAX_SEEK_COUNT = validRange ? validRange.upperLimit - validRange.lowerLimit + 1 : 100;
    if (seekCount >= MAX_SEEK_COUNT) {
      throw new Error(`failed to find workout selection after ${seekCount} tries for settings: `
                            + `${WorkoutSelection.refSettingsString(workoutSettings, areaCategory)} `
                            + `gymType: ${gymType}`);
    }

    const nextSelectionNumber = this.getNextSelectionNumber(currentSelectionNumber, validRange);
    const nextSelectionRefNr = WorkoutSelection.refNr(
      nextSelectionNumber,
      workoutSettings,
      gymType,
      areaCategory,
    );
    const nextWorkoutSelection = await this.workoutSelectionRepository.get(
      nextSelectionRefNr,
    );
    const selectionExistsButIsNotUsable = nextWorkoutSelection
      && !WorkoutSelection.isUsable(nextWorkoutSelection, areaCategory);

    if (!nextWorkoutSelection || selectionExistsButIsNotUsable) {
      return this.findNextValidWorkoutSelectionForCurrentSettings(
        nextSelectionNumber,
        workoutSettings,
        gymType,
        seekCount + 1,
        validRange,
        areaCategory,
      );
    }
    return nextWorkoutSelection;
  }

  private buildDefaultDoc() {
    const defaultDoc: any = {};

    for (const key of Object.keys(GymTypes)) {
      const gymType = GymTypes[key];
      const allSettings = CurrentWorkoutSelectionManager.getAllSettingCombinations(gymType);
      allSettings.forEach((setting: WorkoutSettings) => {
        const keyReference = CurrentWorkoutSelectionManager.activeSelectionId(gymType, setting);
        defaultDoc[keyReference] = -1;
      });
    }

    return defaultDoc;
  }
}
